/** Colour maths for ER table cards, shared by the on-screen TableNode and the direct SVG exporter. */

export type Rgb = [number, number, number];

export function hexToRgb(hex: string): Rgb {
  const clean = hex.replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.replace(/./g, (c) => c + c) : clean, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** Opaque blend of `hex` into a base color, at `amount` (0-1). The table sits visually
 * above its group's own translucent wash — blending onto an opaque base (rather than
 * stacking another semi-transparent tint on top) is what keeps the result matching the
 * group's hue instead of compounding into something more saturated than either alone. */
export function mixRgb(hex: string, base: Rgb, amount: number): Rgb {
  const [r, g, b] = hexToRgb(hex);
  const [br, bg, bb] = base;
  const blend = (c: number, bc: number) => Math.round(bc + (c - bc) * amount);
  return [blend(r, br), blend(g, bg), blend(b, bb)];
}

export const toCss = ([r, g, b]: Rgb) => `rgb(${r}, ${g}, ${b})`;

/** Black on light backgrounds, white on dark ones, picked from the WCAG relative luminance of the actual fill. */
export function readableTextColor([r, g, b]: Rgb): string {
  const lin = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.4 ? '#000000' : '#ffffff';
}

export const WHITE: Rgb = [255, 255, 255];
export const DARK_BODY_BASE: Rgb = [31, 41, 55]; // tailwind gray-800
export const DARK_HEADER_BASE: Rgb = [17, 24, 39]; // tailwind gray-900

/** Resolved colours for a table that inherits `tint` (its own colour or its group's). */
export function tintedTableColors(tint: string, isDark: boolean) {
  const headerFill = mixRgb(tint, isDark ? DARK_HEADER_BASE : WHITE, isDark ? 0.34 : 0.22);
  return {
    body: toCss(mixRgb(tint, isDark ? DARK_BODY_BASE : WHITE, isDark ? 0.22 : 0.12)),
    header: toCss(headerFill),
    headerText: readableTextColor(headerFill),
    border: `${tint}${isDark ? '80' : '55'}`,
  };
}
